// Feature icons
import electricIcon from "../../../images/ev.png";
import payloadIcon from "../../../images/payload.png";
import towingIcon from "../../../images/tow.png";
import runtimeIcon from "../../../images/run.png";
import autonomousIcon from "../../../images/autonomous.png";
import connectivityIcon from "../../../images/connectivity.png";

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Paper,
  Fade,
  Grow,
  Zoom,
} from "@mui/material";
import {
  ChevronRight,
  FiberManualRecord,
} from "@mui/icons-material";
import { styled, keyframes } from "@mui/material/styles";
import robog from "../../../images/product-img-2.jpg";


const PRODUCT_CONTENT = [
  {
    id: 1,
    title: "AUTOMIST500x",
    subtitle: "Next-Generation Agricultural Automation",
    descriptions: [
      "50 - 70% input savings compared to tractor sprayings",
      "Full 360° spray coverage",
      "4WD and Rugged Terrain Mobility",
      "customizable to your application",
    ],
    features: [
      { image: electricIcon, label: "Fully Electric" },
      { image: payloadIcon, label: "500-1000 Kg (1100-2200 lbs) Payload" },
      { image: towingIcon, label: "Towing capacity of 3 Tons \n(6600 lbs)" },
      { image: runtimeIcon, label: "8-12hr Runtime" },
      { image: autonomousIcon, label: "Autonomous Operation" },
      { image: connectivityIcon, label: "4G/5G connectivity" },
    ],
    redirectLink: "/agri-robots",
    img: robog,
  },
  // {
  //   id: 2,
  //   title: "SOLAR PANEL CLEANING ROBOT",
  //   descriptions: [
  //     "Autonomous and tailored solution for your warehouse",
  //     "Increased efficiency",
  //     "Reduced labour cost",
  //     "Improved safety and better accuracy in material handling",
  //   ],
  //   color: "red",
  //   redirectLink: "/logistic-robots",
  //   img: logisticsRobotsImg,
  // },
  // {
  //   id: 3,
  //   title: "SERVICE",
  //   descriptions: [
  //     "Autonomous and tailored solution for your hospitals, restaurants, etc.",
  //     "Easy to use",
  //     "Medical graded",
  //     "Reduced labour cost and improve efficiency",
  //   ],
  //   redirectLink: "service-robots",
  //   color: "blue",
  //   img: serviceRobotsImg,
  // },
];

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// Styled Components
const BackgroundBox = styled(Box)(({ theme }) => ({
  position: "relative",
  minHeight: "100vh",
  background: "linear-gradient(135deg, #000000 0%, #0a0a0a 50%, #000000 100%)",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: "50%",
    height: "50%",
    background: "radial-gradient(circle, rgba(193, 227, 40, 0.15) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "50%",
    height: "50%",
    background: "radial-gradient(circle, rgba(193, 227, 40, 0.15) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const FloatingParticle = styled(Box)(({ delay, duration, left, top }) => ({
  position: "absolute",
  width: 4,
  height: 4,
  borderRadius: "50%",
  background: "rgba(193, 227, 40, 0.3)",
  left: `${left}%`,
  top: `${top}%`,
  animation: `${pulse} ${duration}s ease-in-out infinite`,
  animationDelay: `${delay}s`,
}));

const GlowCard = styled(Card)(({ theme }) => ({
  position: "relative",
  background: "rgba(0, 0, 0, 0.9)",
  backdropFilter: "blur(20px)",
  borderRadius: 24,
  border: "1px solid rgba(193, 227, 40, 0.3)",
  overflow: "hidden",
  transition: "all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: -2,
    // background: "linear-gradient(135deg,rgb(22, 33, 29) 0%,rgb(15, 25, 24) 50%,rgb(33, 42, 44) 100%)",
    borderRadius: 26,
    opacity: 0,
    filter: "blur(25px)",
    transition: "all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    zIndex: -1,
  },
  "&:hover": {
    transform: "translateY(-8px) scale(1.02)",
    borderColor: "rgba(193, 227, 40, 0.4)",
    boxShadow: "0 20px 60px rgba(193, 227, 40, 0.2), 0 8px 32px rgba(0, 0, 0, 0.4)",
    "&::before": {
      opacity: 0.4,
      transform: "scale(1.05)",
    },
  },
}));

const ProductImage = styled(Box)(({ img, isHovered, theme }) => ({
  position: "relative",
  height: 600,
  overflow: "hidden",
  [theme.breakpoints.down('md')]: {
    height: 400,
  },
  [theme.breakpoints.down('sm')]: {
    height: 300,
  },
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    transform: isHovered ? "scale(1.08) rotate(0.5deg)" : "scale(1)",
    filter: isHovered ? "brightness(1.1) contrast(1.05)" : "brightness(1) contrast(1)",
  },
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.6)",
  backdropFilter: "blur(12px)",
  borderRadius: 16,
  padding: theme.spacing(2.5),
  border: "1px solid rgba(193, 227, 40, 0.3)",
  transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(1.5),
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(193, 227, 40, 0.1) 0%, rgba(193, 227, 40, 0.05) 100%)",
    opacity: 0,
    transition: "opacity 0.4s ease",
    zIndex: 0,
  },
  "& > *": {
    position: "relative",
    zIndex: 1,
  },
  "&:hover": {
    borderColor: "rgba(193, 227, 40, 0.6)",
    transform: "translateY(-6px) scale(1.03)",
    background: "rgba(0, 0, 0, 0.8)",
    boxShadow: "0 12px 24px rgba(193, 227, 40, 0.15), 0 4px 12px rgba(0, 0, 0, 0.2)",
    "&::before": {
      opacity: 1,
    },
    "& svg": {
      transform: "scale(1.1) rotate(5deg)",
      filter: "drop-shadow(0 0 8px rgba(193, 227, 40, 0.6))",
    },
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #c1e328 0%, #a0c020 100%)",
  borderRadius: 16,
  padding: "16px 40px",
  fontSize: "1.1rem",
  fontWeight: 600,
  textTransform: "none",
  color: "#000",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  border: "1px solid rgba(193, 227, 40, 0.2)",
  boxShadow: "0 4px 16px rgba(193, 227, 40, 0.2)",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%)",
    opacity: 0,
    transition: "opacity 0.4s ease",
  },
  "&:hover": {
    background: "linear-gradient(135deg, #d0f030 0%, #c1e328 100%)",
    boxShadow: "0 12px 32px rgba(193, 227, 40, 0.4), 0 4px 16px rgba(0, 0, 0, 0.2)",
    transform: "translateY(-3px) scale(1.02)",
    borderColor: "rgba(193, 227, 40, 0.4)",
    "&::after": {
      opacity: 1,
    },
  },
  "& .MuiButton-endIcon": {
    transition: "transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  },
  "&:hover .MuiButton-endIcon": {
    transform: "translateX(6px) scale(1.1)",
  },
}));

const ProductCard = ({ product, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  return (
    <BackgroundBox
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 4, sm: 6, md: 10 },
        px: { xs: 2, sm: 4, md: 8 },
      }}
    >
      {/* Floating Particles */}
      {[...Array(20)].map((_, i) => (
        <FloatingParticle
          key={i}
          delay={Math.random() * 3}
          duration={2 + Math.random() * 3}
          left={Math.random() * 100}
          top={Math.random() * 100}
        />
      ))}

      <Container maxWidth="xl">
        {/* Section Header */}
        {/* <Fade in timeout={1000}>
          <Box textAlign="center" mb={8}>
            <Chip
              icon={<FiberManualRecord sx={{ fontSize: 12, animation: `${pulse} 2s ease-in-out infinite` }} />}
              label="INNOVATION IN MOTION"
              sx={{
                background: "rgba(193, 227, 40, 0.1)",
                border: "1px solid rgba(193, 227, 40, 0.2)",
                backdropFilter: "blur(8px)",
                color: "#c1e328",
                fontWeight: 600,
                letterSpacing: 1.5,
                fontSize: { xs: "0.5rem", md: "0.675rem" },
                mb: 3,
              }}
            />
            {/* <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "2.0rem", sm: "2.5rem", lg: "3.5rem" },
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: -1,
                  mb: 2,
                }}
            >
              Our{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(135deg, #c1e328 0%, #a0c020 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Products
              </Box>
            </Typography> */}
            {/* <Typography  variant="h6"
            sx={{
              color: "rgba(148, 163, 184, 1)",
              maxWidth: 700,
              mx: "auto",
              lineHeight: 1.8,
              fontSize: { xs: "0.875rem", md: "1rem" },
            }}>
              Pioneering the future of autonomous robotics with cutting-edge technology
            </Typography> */}
          {/* </Box>
        </Fade>  */}

        {/* Product Card */}
        <Grow in timeout={1200}>
          <GlowCard
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Grid container>
              {/* Image Section */}
              <Grid item xs={12} lg={6}>
                <ProductImage img={product.img} isHovered={isHovered}>
                  <img src={product.img} alt={product.title}/>
                  
                  {/* Buy Now Button */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      p: { xs: 2, sm: 3, md: 4 },
                      pl: { xs: 2, sm: 4, md: 6 },
                      zIndex: 2,
                      background: "linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.95) 100%)",
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                      justifyContent: "space-between",
                      gap: { xs: 2, sm: 3 },
                    }}
                  >
                    <Fade in timeout={1200}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgb(255, 255, 255)",
                          fontWeight: 600,
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          letterSpacing: 0.3,
                          lineHeight: 1.5,
                          flex: { xs: "none", sm: 1 },
                          textAlign: { xs: "center", sm: "left" },
                        }}
                      >
                        Let's take care of your farms !!
                      </Typography>
                    </Fade>
                    <Zoom in timeout={1400}>
                      <GradientButton
                        variant="contained"
                        size="medium"
                        endIcon={<ChevronRight />}
                        onClick={() => navigate('/contact')}
                        sx={{
                          px: { xs: 3, sm: 3.5 },
                          py: { xs: 1, sm: 1.2 },
                          fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                          whiteSpace: "nowrap",
                          width: { xs: "100%", sm: "auto" },
                        }}
                      >
                        Buy now
                      </GradientButton>
                    </Zoom>
                  </Box>
                </ProductImage>
              </Grid>

              {/* Content Section */}
              <Grid item xs={12} lg={6}>
                <CardContent sx={{ pt: { xs: 3, sm: 3, md: 3 }, pb: { xs: 3, sm: 4, md: 6 }, px: { xs: 2, sm: 3, md: 6 }, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <Stack spacing={{ xs: 1.5, sm: 2 }}>
                    {/* Title */}
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{
                          color: "#c1e328",
                          fontWeight: 700,
                          letterSpacing: { xs: 1, sm: 1.5, md: 2 },
                          fontSize: { xs: "0.625rem", sm: "0.688rem", md: "0.75rem" },
                          mb: 0.5,
                        }}
                      >
                        {product.subtitle}
                      </Typography>
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: { xs: "1.5rem", sm: "2rem", md: "3rem" },
                          fontWeight: 900,
                          color: "#fff",
                          letterSpacing: { xs: 1, sm: 1.5, md: 2 },
                          fontFamily: "'Avenir Next Condensed Heavy', 'Avenir Next Condensed Extra Bold', 'Avenir Next Condensed', 'Arial Black', sans-serif",
                        }}
                      >
                        {product.title}
                      </Typography>
                    </Box>

                    {/* Feature Grid */}
                    <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
                      {product.features.map((feature, idx) => {
                        return (
                          <Grid item xs={6} key={idx}>
                            <Zoom in timeout={1000 + idx * 200}>
                              <FeatureCard 
                                elevation={0} 
                                sx={{ 
                                  p: { xs: 1.5, sm: 2, md: 2.5 }, 
                                  gap: { xs: 1, sm: 1.5 },
                                  minHeight: { xs: 70, sm: 80, md: 80 }, // Ensures all boxes are same height
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <img 
                                  src={feature.image} 
                                  alt={feature.label}
                                  style={{ 
                                    width: window.innerWidth < 600 ? 18 : 24, 
                                    height: window.innerWidth < 600 ? 18 : 24, 
                                    objectFit: "contain",
                                    flexShrink: 0
                                  }} 
                                />
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: "rgba(203, 213, 225, 1)", 
                                    fontWeight: 500,
                                    fontSize: { xs: "0.625rem", sm: "0.875rem" },
                                    lineHeight: { xs: 1.3, sm: 1.4 },
                                    whiteSpace: "pre-line" // Allows manual line breaks with \n
                                  }}
                                >
                                  {feature.label}
                                </Typography>
                              </FeatureCard>
                            </Zoom>
                          </Grid>
                        );
                      })}
                    </Grid>

                    {/* Descriptions */}
                    <Stack spacing={{ xs: 1, sm: 1.5 }}>
                      {product.descriptions.map((desc, idx) => (
                        <Fade in timeout={1500 + idx * 200} key={idx}>
                          <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2 }, alignItems: "flex-start" }}>
                            <FiberManualRecord
                              sx={{
                                fontSize: { xs: 6, sm: 8 },
                                color: "#c1e328",
                                mt: { xs: 0.8, sm: 1.2 },
                                transition: "transform 0.3s ease",
                                "&:hover": { transform: "scale(1.5)" },
                                flexShrink: 0
                              }}
                            />
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                color: "rgba(203, 213, 225, 1)", 
                                lineHeight: 1.8,
                                fontSize: { xs: "0.875rem", sm: "1rem" }
                              }}
                            >
                              {desc}
                            </Typography>
                          </Box>
                        </Fade>
                      ))}
                    </Stack>
                  </Stack>
                </CardContent>
              </Grid>
            </Grid>
          </GlowCard>
        </Grow>

        {/* Bottom Accent */}
        <Fade in timeout={2000}>
          <Box sx={{ mt: { xs: 4, sm: 6, md: 8 }, textAlign: "center", px: { xs: 2, sm: 0 } }}>
            <Paper
              sx={{
                display: "inline-flex",
                alignItems: "center",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 3, md: 4 },
                px: { xs: 3, sm: 4 },
                py: { xs: 2, sm: 2 },
                borderRadius: { xs: 5, sm: 50 },
                background: "rgba(0, 0, 0, 0.7)",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(193, 227, 40, 0.5)",
              }}
              elevation={0}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FiberManualRecord sx={{ fontSize: { xs: 10, sm: 12 }, color: "#c1e328", animation: `${pulse} 2s ease-in-out infinite` }} />
                <Typography variant="body2" sx={{ color: "rgba(148, 163, 184, 1)", fontSize: { xs: "0.8125rem", sm: "0.875rem" } }}>
                  Autonomous
                </Typography>
              </Box>
              <Box sx={{ width: { xs: 48, sm: 1 }, height: { xs: 1, sm: 24 }, bgcolor: "rgba(193, 227, 40, 0.3)" }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FiberManualRecord sx={{ fontSize: { xs: 10, sm: 12 }, color: "#c1e328", animation: `${pulse} 2s ease-in-out infinite` }} />
                <Typography variant="body2" sx={{ color: "rgba(148, 163, 184, 1)", fontSize: { xs: "0.8125rem", sm: "0.875rem" } }}>
                  Sustainable
                </Typography>
              </Box>
              <Box sx={{ width: { xs: 48, sm: 1 }, height: { xs: 1, sm: 24 }, bgcolor: "rgba(193, 227, 40, 0.3)" }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FiberManualRecord sx={{ fontSize: { xs: 10, sm: 12 }, color: "#c1e328", animation: `${pulse} 2s ease-in-out infinite` }} />
                <Typography variant="body2" sx={{ color: "rgba(148, 163, 184, 1)", fontSize: { xs: "0.8125rem", sm: "0.875rem" } }}>
                  Intelligent
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Fade>
      </Container>
    </BackgroundBox>
  );
};

const HomepageProduct = () => {
  return (
    <Box>
      {PRODUCT_CONTENT.map((product, index) => (
        <ProductCard key={product.id} product={product} index={index} />
      ))}
    </Box>
  );
};

export default HomepageProduct;